import React, { useEffect, useState, useContext, useCallback, useRef } from 'react';
import Cards from '../components/Cards/Cards';
import EscalationTable from '../components/Tables/EscalationTable';
import { localurl } from '../components/utils';
import axios from 'axios';
import { Select, message } from "antd";
import { useNavigate } from 'react-router-dom';
import { WebSocketContext } from '../context/WebSocketContext';

const Escalations = () => {
  const navigate = useNavigate();
  const { messages } = useContext(WebSocketContext);
  const [data, setData] = useState();
  const [user, setUser] = useState([]);
  const [escalationData, setEscalationData] = useState([]);
  const [totalRows, setTotalRows] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [searchQuery, setSearchQuery] = useState("");
  const [status, setStatus] = useState("all");
  const [priority, setPriority] = useState("all");
  const [selectedUser, setSelectedUser] = useState("all");
  const [assignModalOpen, setAssignModalOpen] = useState(false);
  const [selectedTicketId, setSelectedTicketId] = useState(null);
  const [selectedAgent, setSelectedAgent] = useState("");
  const [priorityModalOpen, setPriorityModalOpen] = useState(false);
  const [selectedPriorityTicketId, setSelectedPriorityTicketId] = useState(null);
  const [newPriority, setNewPriority] = useState('low');
  const [activeCustomerIds, setActiveCustomerIds] = useState([]);
  const [activeCustomerIPs, setActiveCustomerIPs] = useState([]);
  const [websocketDataLoaded, setWebsocketDataLoaded] = useState(false);
  
  // Auto-reload states
  const [lastEscalationCount, setLastEscalationCount] = useState(0);
  const [newEscalationsCount, setNewEscalationsCount] = useState(0);
  const [isAutoReloading, setIsAutoReloading] = useState(false);
  const [autoReloadEnabled] = useState(true);
  const pollingIntervalRef = useRef(null);
  const lastFetchTimeRef = useRef(Date.now());
  
  // Loading states
  const [isAssigning, setIsAssigning] = useState(false);
  const [isUpdatingPriority, setIsUpdatingPriority] = useState(false);
  const [isMarkingResolved, setIsMarkingResolved] = useState(false);

  const onViewDetails = (ticketId) => {
    navigate(`/conversation/${ticketId}`);
  };

  const fetchData = async () => {
    try {
      const response = await axios.get(`${localurl}/escalation/info`, {
        headers: {
          accept: "application/json",
        }
      });
      setData(response.data);
    } catch (error) {
      console.error("Error fetching summary data:", error);
      message.error("Failed to fetch summary data");
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // Track active customer IDs and IPs from WebSocket messages
  useEffect(() => {
    if (messages && messages.length > 0) {
      // Get all active sessions from WebSocket messages
      const activeSessions = messages
        .filter(item => Array.isArray(item?.active_sessions))
        .flatMap(item => item.active_sessions);

      // Get unique customer IDs
      const ids = [...new Set(activeSessions
        .map(session => session?.customer_id)
        .filter(id => id !== undefined && id !== null))];

      // Get unique IP addresses
      const ips = [...new Set(activeSessions
        .map(session => session?.ip)
        .filter(ip => ip !== undefined && ip !== null))];

      
      setActiveCustomerIds(ids);
      setActiveCustomerIPs(ips);
      setWebsocketDataLoaded(true);
    }
  }, [messages]);

  const handleChangePriorityClick = (ticketId) => {
    // Find the current ticket data to get the currently assigned priority
    const currentTicket = escalationData.data?.find(ticket => ticket.ticket_id === ticketId);
    const currentPriority = currentTicket?.priority || 'low';
    
    setSelectedPriorityTicketId(ticketId);
    setNewPriority(currentPriority); // Set the current priority as selected
    setPriorityModalOpen(true);
  };

  const updateTicketPriority = async () => {
    if (!selectedPriorityTicketId || !newPriority) {
      message.error("Please select a priority");
      return;
    }

    setIsUpdatingPriority(true);
    try {
      await axios.post(
        `${localurl}/escalation/update/priority`,
        {
          ticket_id: selectedPriorityTicketId,
          priority: newPriority
        },
        {
          headers: {
            accept: "application/json",
          }
        }
      );
      
      message.success("Priority updated successfully!");
      setPriorityModalOpen(false);
      setSelectedPriorityTicketId(null);
      setNewPriority('low');
      
      // Refresh data
      await Promise.all([
        fetchEscalationData(rowsPerPage, page * rowsPerPage, searchQuery, status, priority, selectedUser),
        fetchData()
      ]);
    } catch (error) {
      console.error("Error updating priority:", error);
      message.error("Failed to update priority. Please try again.");
    } finally {
      setIsUpdatingPriority(false);
    }
  };

  const handleAssignAgentClick = (ticketId) => {
    // Find the current ticket data to get the currently assigned agent
    const currentTicket = escalationData.data?.find(ticket => ticket.ticket_id === ticketId);
    const currentAgent = currentTicket?.assign_to || "";
    
    setSelectedTicketId(ticketId);
    setSelectedAgent(currentAgent); // Set the current agent as selected
    setAssignModalOpen(true);
  };

  const markTicketResolved = async (ticket_id) => {
    setIsMarkingResolved(true);
    try {
      await axios.post(
        `${localurl}/escalation/update/status`,
        {
          ticket_id,
          status: "resolved"
        },
        {
          headers: {
            accept: "application/json",
          }
        }
      );
      
      message.success("Ticket marked as resolved successfully!");
      
      // Refresh data
      await Promise.all([
        fetchEscalationData(rowsPerPage, page * rowsPerPage, searchQuery, status, priority, selectedUser),
        fetchData()
      ]);
    } catch (error) {
      console.error("Failed to mark ticket as resolved:", error);
      message.error("Failed to mark ticket as resolved. Please try again.");
    } finally {
      setIsMarkingResolved(false);
    }
  };

  const assignAgentToTicket = async () => {
    if (!selectedAgent) {
      message.error("Please select an agent");
      return;
    }

    setIsAssigning(true);
    try {
      await axios.post(`${localurl}/escalation/update/assign_to`, {
        ticket_id: selectedTicketId,
        assign_to: selectedAgent
      }, {
        headers: {
          accept: "application/json",
        }
      });
      
      message.success("Agent assigned successfully!");
      setAssignModalOpen(false);
      setSelectedAgent("");
      setSelectedTicketId(null);
      
      // Refresh data
      await Promise.all([
        fetchEscalationData(rowsPerPage, page * rowsPerPage, searchQuery, status, priority),
        fetchData()
      ]);
    } catch (error) {
      console.error("Error assigning agent:", error);
      message.error("Failed to assign agent. Please try again.");
    } finally {
      setIsAssigning(false);
    }
  };

  const fetchUser = async () => {
    try {
      const response = await axios.get(`${localurl}/active/users`, {
        headers: {
          accept: "application/json",
        }
      });
      setUser(response.data.usernames);
    } catch (error) {
      console.error("Error fetching summary data:", error);
    }
  };

  useEffect(() => {
    fetchUser()
  }, [])

  const fetchEscalationData = useCallback(async (limit, offset, ticket_id = "", status = "", priority = "", agent = "", isAutoReload = false) => {
    try {
      const params = {
        limit,
        offset,
        ticket_id: ticket_id || undefined,
        status: status !== "all" ? status : undefined,
        priority: priority !== "all" ? priority : undefined,
        agents: agent !== "all" ? agent : undefined,
      };
      
      const response = await axios.get(`${localurl}/escalation`, {
        params,
        headers: {
          accept: "application/json",
        },
      });
      
      // Check for new escalations if this is an auto-reload
      if (isAutoReload && response.data.total_count > lastEscalationCount) {
        const newCount = response.data.total_count - lastEscalationCount;
        setNewEscalationsCount(prev => prev + newCount);
        message.success(`${newCount} new escalation${newCount > 1 ? 's' : ''} detected!`);
      }
      
      setEscalationData(response.data);
      setTotalRows(response.data.total_count);
      setLastEscalationCount(response.data.total_count);
      lastFetchTimeRef.current = Date.now();
    } catch (error) {
      console.error("Error fetching escalation data:", error);
      if (!isAutoReload) {
        message.error("Failed to fetch escalation data");
      }
    }
  }, [lastEscalationCount]);

  useEffect(() => {
    fetchEscalationData(rowsPerPage, page, searchQuery, status, priority, selectedUser);
  }, [page, rowsPerPage, searchQuery, status, priority, selectedUser, fetchEscalationData]);

  // Auto-reload polling mechanism with 5-second interval
  useEffect(() => {
    if (!autoReloadEnabled) return;

    const startPolling = () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }

      pollingIntervalRef.current = setInterval(async () => {
        if (isAssigning || isUpdatingPriority || isMarkingResolved) return;
        
        setIsAutoReloading(true);
        await fetchEscalationData(rowsPerPage, page * rowsPerPage, searchQuery, status, priority, selectedUser, true);
        await fetchData();
        setIsAutoReloading(false);
      }, 5000); // Poll every 5 seconds
    };

    startPolling();

    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
    };
  }, [autoReloadEnabled, rowsPerPage, page, searchQuery, status, priority, selectedUser, fetchEscalationData, isAssigning, isUpdatingPriority, isMarkingResolved]);

  // Monitor WebSocket messages for escalation-related updates
  useEffect(() => {
    if (!messages || messages.length === 0) return;

    const latestMessage = messages[messages.length - 1];
    
    // Check if the message contains escalation-related data
    if (latestMessage && (
      latestMessage.escalation_queries || 
      latestMessage.escalation_data ||
      latestMessage.new_escalation ||
      (latestMessage.interaction && latestMessage.sentiments)
    )) {
      // Trigger a refresh if we detect escalation-related activity
      if (autoReloadEnabled && !isAssigning && !isUpdatingPriority && !isMarkingResolved) {
        const timeSinceLastFetch = Date.now() - lastFetchTimeRef.current;
        // Only refresh if it's been at least 3 seconds since last fetch to avoid spam
        if (timeSinceLastFetch > 3000) {
          setIsAutoReloading(true);
          fetchEscalationData(rowsPerPage, page * rowsPerPage, searchQuery, status, priority, selectedUser, true);
          fetchData();
          setIsAutoReloading(false);
        }
      }
    }
  }, [messages, autoReloadEnabled, rowsPerPage, page, searchQuery, status, priority, selectedUser, fetchEscalationData, isAssigning, isUpdatingPriority, isMarkingResolved]);


  const escalationColumn = [
    { header: "Id", accessor: "ticket_id" },
    { header: "Customer", accessor: "Customer"},
    { header: "Date", accessor: "date" },
    { header: "Priority", accessor: "priority" },
    { header: "Status", accessor: "status" },
    { header: "Agent", accessor: "agent" },
  ];

  return (
    <div className='flex flex-col w-full'>
      <div className="flex justify-between items-center mb-3">
        <h1 className="text-black font-bold text-2xl">Escalations</h1>
        <div className="flex items-center gap-3">
          {newEscalationsCount > 0 && (
            <div className="flex items-center gap-2 px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm font-medium">
              <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
              {newEscalationsCount} new
            </div>
          )}
        </div>
      </div>
      <p className="font-normal text-base text-gray-600 mb-10">
        Manage conversations that require human intervention
      </p>
      <div className="flex flex-row justify-between gap-5 max-lg:flex-col mt-6">
        <Cards category={"Total Escalations"} number={data?.total_escalated} percent={"12%"} previous={"from last month"} percentColor={"text-green"} />
        <Cards category={"Pending"} number={data?.pending} previous={"Awaiting agent assignment"} />
        <Cards category={"In Progress"} number={data?.in_progress} previous={"Currently being handled"} />
        <Cards category={"Resolved"} number={data?.resolved} previous={"Completed in the last 30 days"} />
      </div>
      <div className='mt-6 p-3 pb-6 rounded-xl bg-white shadow-md'>
        <EscalationTable
          data={escalationData.data}
          columns={escalationColumn}
          user={user}
          totalRows={totalRows}
          page={page}
          rowsPerPage={rowsPerPage}
          handleChangePage={(event, newPage) => {
            setPage(newPage);
          }}
          handleChangeRowsPerPage={(event) => {
            setRowsPerPage(parseInt(event.target.value, 10));
            setPage(0);
          }}
          setSearchQuery={setSearchQuery}
          status={status}
          setStatus={setStatus}
          priority={priority}
          setPriority={setPriority}
          setSelectedUser={setSelectedUser}
          selectedUser={selectedUser}
          onAssignAgent={handleAssignAgentClick}
          onMarkResolved={markTicketResolved}
          onChangePriority={handleChangePriorityClick}
          onViewDetails={onViewDetails}
          isAssigning={isAssigning}
          isUpdatingPriority={isUpdatingPriority}
          isMarkingResolved={isMarkingResolved}
          activeCustomerIds={activeCustomerIds}
          activeCustomerIPs={activeCustomerIPs}
          websocketDataLoaded={websocketDataLoaded}
          messages={messages}
          isAutoReloading={isAutoReloading}
          autoReloadEnabled={autoReloadEnabled}
          newEscalationsCount={newEscalationsCount}
        />
      </div>
      {assignModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex justify-center items-center z-50">
          <div className="bg-white p-6 rounded-xl w-96">
            <h2 className="text-lg font-semibold mb-4">Assign Agent</h2>
            <Select
              className="w-full mb-4"
              value={selectedAgent}
              onChange={(value) => setSelectedAgent(value)}
              options={user.map(user => ({ label: user, value: user }))}
              placeholder="Select an agent"
              disabled={isAssigning}
            />
            <div className="flex justify-end gap-2">
              <button 
                onClick={() => setAssignModalOpen(false)} 
                className="px-4 py-2 border rounded-md"
                disabled={isAssigning}
              >
                Cancel
              </button>
              <button 
                onClick={assignAgentToTicket} 
                className={`px-4 py-2 text-white rounded-md flex items-center gap-2 ${
                  isAssigning ? 'bg-gray-400 cursor-not-allowed' : 'bg-[#c5003e] hover:bg-[#a40031]'
                }`}
                disabled={isAssigning}
              >
                {isAssigning ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    Assigning...
                  </>
                ) : (
                  'Assign'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
      {priorityModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex justify-center items-center z-50">
          <div className="bg-white p-6 rounded-xl w-96">
            <h2 className="text-lg font-semibold mb-4">Change Priority</h2>
            <Select
              className="w-full mb-4"
              value={newPriority}
              onChange={(value) => setNewPriority(value)}
              options={[
                { label: "Low", value: "low" },
                { label: "Medium", value: "medium" },
                { label: "High", value: "high" },
              ]}
              placeholder="Select priority"
              disabled={isUpdatingPriority}
            />
            <div className="flex justify-end gap-2">
              <button 
                onClick={() => setPriorityModalOpen(false)} 
                className="px-4 py-2 border rounded-md"
                disabled={isUpdatingPriority}
              >
                Cancel
              </button>
              <button 
                onClick={updateTicketPriority} 
                className={`px-4 py-2 text-white rounded-md flex items-center gap-2 ${
                  isUpdatingPriority ? 'bg-gray-400 cursor-not-allowed' : 'bg-[#c5003e] hover:bg-[#a40031]'
                }`}
                disabled={isUpdatingPriority}
              >
                {isUpdatingPriority ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    Updating...
                  </>
                ) : (
                  'Update Priority'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Escalations;
